import { useEffect, useState, type ReactElement } from "react";
import { supabase } from "@baseline/web/supabase";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ContentHeader,
	StatusIndicator,
	type ProcessingStatus,
} from "../components";
import { MarkdownRenderer } from "../components/MarkdownRenderer";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useLibraryItem } from "../hooks/useLibraryItem";
import { validateHighlightSearch } from "../lib/validateHighlightSearch";

export const Route = createFileRoute("/content/$contentId")({
	component: ContentDetailPage,
	validateSearch: validateHighlightSearch,
});

interface ContentData {
	id: string;
	canonical_url: string;
	title: string | null;
	description: string | null;
	og_image_url: string | null;
	channel_name: string | null;
	published_at: string | null;
	status: ProcessingStatus;
	error_message: string | null;
	markdown: string | null;
	notes: string | null;
	content: string | null;
}

function ContentDetailPage(): ReactElement {
	const { contentId } = Route.useParams();
	const { highlight, chunkIndex } = Route.useSearch();
	const queryClient = useQueryClient();
	const { libraryItem, tags, removeFromLibrary } = useLibraryItem({
		contentId,
	});

	const retry = useMutation({
		mutationFn: async (url: string) => {
			const { error } = await supabase.functions.invoke("process-url", {
				body: { url },
			});
			if (error) throw new Error(error.message || "Retry failed");
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["content", contentId],
			});
		},
	});

	const contentQuery = useQuery({
		queryKey: ["content", contentId],
		queryFn: async (): Promise<ContentData | null> => {
			const { data, error } = await supabase
				.from("contents")
				.select(
					`
					id,
					canonical_url,
					title,
					description,
					og_image_url,
					channel_name,
					published_at,
					status,
					error_message,
					markdown,
					notes,
					content
				`,
				)
				.eq("id", contentId)
				.single();

			if (error) {
				if (error.code === "PGRST116") {
					return null;
				}
				throw error;
			}
			return data as unknown as ContentData;
		},
		refetchInterval: (query) => {
			const status = query.state.data?.status;
			const isProcessing =
				status === "pending" || status === "processing";
			return isProcessing ? 2000 : false;
		},
	});

	useEffect(() => {
		const contentChannel = supabase
			.channel(`content:${contentId}`)
			.on(
				"postgres_changes",
				{
					event: "UPDATE",
					schema: "public",
					table: "contents",
					filter: `id=eq.${contentId}`,
				},
				() => {
					queryClient.invalidateQueries({
						queryKey: ["content", contentId],
					});
				},
			)
			.subscribe();

		return () => {
			supabase.removeChannel(contentChannel);
		};
	}, [contentId, queryClient]);

	const content = contentQuery.data;
	const [showRaw, setShowRaw] = useState(false);

	useDocumentTitle(content?.title);

	if (contentQuery.isPending) {
		return <div className="mx-auto max-w-[960px] px-6 py-10" />;
	}

	if (contentQuery.data === null) {
		return (
			<div className="mx-auto max-w-[960px] px-6 py-10">
				<div className="border-line bg-surface-raised rounded-lg border p-8 text-center">
					<h1 className="font-display text-fg-strong text-xl">
						Content not found
					</h1>
					<p className="text-fg-faint mt-2">
						This content may not exist or you don't have access to
						it.
					</p>
					<Link
						to="/"
						search={{
							mode: undefined,
							tag: undefined,
							type: undefined,
							summary: undefined,
						}}
						className="text-accent hover:text-fg-strong mt-4 inline-block transition-colors"
					>
						Go back home
					</Link>
				</div>
			</div>
		);
	}

	if (contentQuery.isError) {
		return (
			<div className="mx-auto max-w-[960px] px-6 py-10">
				<div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center">
					<h1 className="text-xl text-red-400">Error</h1>
					<p className="mt-2 text-red-500">
						{contentQuery.error instanceof Error
							? contentQuery.error.message
							: "Failed to load content"}
					</p>
					<Link
						to="/"
						search={{
							mode: undefined,
							tag: undefined,
							type: undefined,
							summary: undefined,
						}}
						className="text-accent hover:text-fg-strong mt-4 inline-block transition-colors"
					>
						Go back home
					</Link>
				</div>
			</div>
		);
	}

	// All null/error/pending states handled above — content is guaranteed non-null
	const data = content!;
	const isProcessing =
		data.status === "pending" || data.status === "processing";

	const isYouTube = !!data.channel_name;
	// CONTEXT: notes = LLM digest (both web and YouTube), markdown = raw HTML-to-md (web only).
	// Raw toggle: YouTube shows transcript (content), web shows original markdown.
	const displayContent = data.notes || data.markdown;
	const rawContent = isYouTube ? data.content : data.markdown;
	const canToggle = isYouTube
		? !!data.notes && !!data.content
		: !!data.notes && !!data.markdown;
	const shownContent = showRaw ? rawContent : displayContent;

	return (
		<div className="pb-24">
			<section className="mx-auto max-w-[960px] px-6 pt-8">
				<ContentHeader
					title={data.title}
					thumbnailUrl={data.og_image_url}
					sourceUrl={data.canonical_url}
					author={data.channel_name}
					publishedAt={data.published_at}
					markdown={data.markdown}
					tags={tags}
					libraryItemId={libraryItem?.id}
					shareToken={libraryItem?.share_token}
					contentId={contentId}
					onRemove={
						libraryItem
							? () => removeFromLibrary.mutate(libraryItem.id)
							: undefined
					}
					canToggle={canToggle}
					showRaw={showRaw}
					onToggleRaw={() => setShowRaw((v) => !v)}
				/>
			</section>

			{isProcessing && (
				<div
					data-testid="processing-indicator"
					className="mx-auto max-w-[960px] px-6 pt-8"
				>
					<div className="border-line bg-surface-raised mb-8 rounded-lg border px-5 py-3">
						<StatusIndicator status={data.status} />
					</div>
				</div>
			)}

			{data.status === "failed" && (
				<div className="mx-auto max-w-[960px] px-6">
					<div className="mb-8 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 p-5">
						<p className="text-red-400">
							Something went wrong while processing this URL.
						</p>
						<button
							type="button"
							onClick={() => retry.mutate(data.canonical_url)}
							disabled={retry.isPending}
							className="text-fg-tertiary hover:text-fg-strong border-line-strong hover:border-fg-ghost ml-4 shrink-0 rounded border px-3 py-1.5 text-sm transition-colors disabled:opacity-50"
						>
							{retry.isPending ? "Retrying..." : "Try again"}
						</button>
					</div>
				</div>
			)}

			<div className="mx-auto max-w-[960px] px-6">
				{isProcessing && !shownContent && (
					<div className="space-y-3">
						<div className="bg-skeleton h-4 w-full animate-pulse rounded" />
						<div className="bg-skeleton h-4 w-5/6 animate-pulse rounded" />
						<div className="bg-skeleton h-4 w-4/6 animate-pulse rounded" />
						<div className="bg-skeleton mt-6 h-4 w-full animate-pulse rounded" />
						<div className="bg-skeleton h-4 w-3/4 animate-pulse rounded" />
					</div>
				)}

				{shownContent && (
					<div data-testid="markdown-content">
						<MarkdownRenderer
							markdown={shownContent}
							highlight={highlight}
							chunkIndex={chunkIndex}
						/>
					</div>
				)}

				{data.status === "completed" && !shownContent && (
					<div className="border-line bg-surface-raised rounded-lg border p-8 text-center">
						<p className="text-fg-faint">
							No content was extracted from this URL.
						</p>
					</div>
				)}
			</div>
		</div>
	);
}
